import moment from 'moment'

type AuthState = {
  isSignedIn: boolean | null
  currentUser?: {
    admin: boolean
    host: boolean
    storyteller: boolean
    unread_conversations_count: number
    id: number
    fullname: string
    first_name: string
    last_name: string
    avatar_url: string
    url: string
    location: string
    description: string
    phone_number_verified: boolean
    phone_number_verified_at: string
    phone_number_verification_enabled: boolean
    email_verified: boolean
    facebook_verified: boolean
    review_count: number
    listing_count: number
    email: string
    phone_number: string
    home_phone_number: string
    confirmed_at: string
    profile_complete: boolean
    invited_to_sign_up?: boolean
    hashid: string
    eligible_for_referrals: boolean
  } | null
  loading: boolean
  updating: boolean
  updated: boolean
  fetched: boolean
  errors?: any
  verifyingPhoneNumber: boolean
  verifyingSMSCode: boolean
  phoneNumberVerificationSent: boolean
  csrfToken: any
  showAuthModal: boolean
  actionToReplay: any
  referredById: string
  referralType: string
}

export type AuthRootState = {
  auth: AuthState
}

const initialState: AuthState = {
  currentUser: null,
  loading: false,
  updating: false,
  updated: false,
  isSignedIn: null,
  fetched: false,
  errors: null,
  verifyingPhoneNumber: false,
  verifyingSMSCode: false,
  phoneNumberVerificationSent: false,
  csrfToken: null,
  showAuthModal: false,
  actionToReplay: null,
  referredById: null,
  referralType: null,
}

type Action = any // needs to be typed

export default (state = initialState, action: Action): AuthState => {
  switch (action.type) {
    case 'CURRENT_USER_FETCH_PENDING':
      return {
        ...state,
        loading: true,
      }

    case 'CURRENT_USER_FETCH_REJECTED':
      return {
        ...state,
        loading: false,
        fetched: true,
        isSignedIn: false,
      }

    case 'CURRENT_USER_FETCH_FULFILLED':
      return {
        ...state,
        currentUser: action.payload.data,
        loading: false,
        fetched: true,
        isSignedIn: !!action.payload.data,
      }

    case 'CURRENT_USER_SILENT_FETCH_FULFILLED':
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          phone_number: action.payload.data.phone_number,
          phone_number_verified: action.payload.data.phone_number_verified,
          phone_number_verified_at:
            action.payload.data.phone_number_verified_at,
        },
      }

    case 'CURRENT_USER_PHONE_NUMBER_VERIFY_PENDING':
      return {
        ...state,
        verifyingPhoneNumber: true,
      }

    case 'CURRENT_USER_PHONE_NUMBER_VERIFY_REJECTED':
      return {
        ...state,
        verifyingPhoneNumber: true,
        phoneNumberVerificationSent: false,
      }

    case 'CURRENT_USER_PHONE_NUMBER_VERIFY_FULFILLED':
      return {
        ...state,
        verifyingPhoneNumber: false,
        phoneNumberVerificationSent: true,
      }

    case 'CURRENT_USER_SMS_CODE_VERIFY_PENDING':
      return {
        ...state,
        verifyingSMSCode: true,
      }

    case 'CURRENT_USER_SMS_CODE_VERIFY_REJECTED':
      return {
        ...state,
        verifyingSMSCode: false,
      }

    case 'CURRENT_USER_SMS_CODE_VERIFY_FULFILLED':
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          phone_number_verified: true,
          phone_number_verified_at: moment.utc().toString(),
        },
        phoneNumberVerificationSent: false,
        verifyingSMSCode: false,
      }

    case 'CURRENT_USER_UPDATE_PENDING':
      return {
        ...state,
        updating: true,
      }

    case 'CURRENT_USER_UPDATE_REJECTED':
      return {
        ...state,
        updating: false,
        updated: false,
        errors: true,
      }

    case 'CURRENT_USER_UPDATE_FULFILLED':
      return {
        ...state,
        currentUser: action.payload.data,
        updating: false,
        updated: true,
        phoneNumberVerificationSent: false,
      }

    case 'CURRENT_USER_RESET_UPDATED':
      return {
        ...state,
        updated: false,
      }

    case 'USER_SIGN_IN_PENDING':
      return {
        ...state,
        loading: true,
      }

    case 'USER_SIGN_IN_REJECTED':
      return {
        ...state,
        loading: false,
        fetched: true,
        isSignedIn: false,
        errors: [action.payload],
      }

    case 'USER_SIGN_IN_FULFILLED':
      return {
        ...state,
        currentUser: action.payload.data,
        loading: false,
        fetched: true,
        isSignedIn: true,
      }

    case 'USER_REGISTER_PENDING':
      return {
        ...state,
        loading: true,
      }

    case 'USER_REGISTER_REJECTED':
      return {
        ...state,
        loading: false,
        fetched: true,
        isSignedIn: false,
        errors: [action.payload],
      }

    case 'USER_REGISTER_FULFILLED':
      return {
        ...state,
        currentUser: action.payload.data,
        loading: false,
        fetched: true,
        isSignedIn: true,
      }

    case 'USER_CHANGE_PASSWORD_PENDING':
      return {
        ...state,
        updating: true,
      }

    case 'USER_CHANGE_PASSWORD_REJECTED':
      return {
        ...state,
        updating: false,
        errors: [action.payload],
      }

    case 'USER_CHANGE_PASSWORD_FULFILLED':
      return {
        ...state,
        updating: false,
        fetched: true,
      }

    case 'AUTH_MODAL_SHOW':
      return {
        ...state,
        showAuthModal: true,
      }

    case 'AUTH_MODAL_HIDE':
      return {
        ...state,
        showAuthModal: false,
        actionToReplay: null,
      }

    case 'AUTH_SET_REFERRING_USER_ID':
      return {
        ...state,
        referredById: action.referredById,
        referralType: action.referralType,
      }

    case 'AUTH_CLEAR_REFERRING_USER_ID':
      return {
        ...state,
        referredById: null,
        referralType: null,
      }

    case 'AUTH_SET_ACTION_TO_REPLAY':
      return {
        ...state,
        actionToReplay: action.payload,
      }

    case 'AUTH_FACEBOOK_LOGIN_PENDING':
      return {
        ...state,
        loading: true,
      }

    case 'AUTH_FACEBOOK_LOGIN_REJECTED':
      return {
        ...state,
        loading: false,
        showAuthModal: false,
        actionToReplay: null,
      }

    case 'AUTH_FACEBOOK_LOGIN_FULFILLED':
      return {
        ...state,
        loading: false,
      }

    default:
      return state
  }
}
